/**
 * Feb-2026 (User-Wunsch 26 May v2) — Anonymes Probefahrt-Anfrage-Modal.
 *
 * Käufer kann OHNE Login eine Probefahrt anfragen. Pflichtfelder:
 * Vorname, Name, E-Mail, Handynummer. Sendet an
 * ``POST /api/test-ride-requests`` (Endpoint ist anonymous-ready).
 */
import { useState } from "react";
import { KeyRound, X, User as UserIcon, Mail, Phone, Calendar, MessageSquare, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import api, { formatApiErrorDetail } from "../../lib/api";

export default function TestRideRequestModal({ listingId, listingTitle, open, onClose, defaults = {}, variant = "testride" }) {
    const isReserve = variant === "reserve";
    const labels = isReserve
        ? {
            title: "Fahrzeug reservieren",
            intro: "Sichern Sie sich dieses Fahrzeug für 48 Stunden. Sie erhalten umgehend einen vorbereiteten Kaufvertrag per E-Mail.",
            submit: "Jetzt reservieren",
            successTitle: "Reservierung gesendet!",
            successBody: "der Händler bekommt deine Reservierungs-Anfrage und meldet sich an",
            toastSuccess: "Fahrzeug reserviert — der Händler meldet sich kurzfristig.",
        }
        : {
            title: "Probefahrt anfragen",
            intro: "Keine Anmeldung erforderlich. Der Händler sieht deine Kontaktdaten und meldet sich per E-Mail oder Telefon.",
            submit: "Anfrage senden",
            successTitle: "Anfrage erfolgreich gesendet!",
            successBody: "der Händler bekommt deine Anfrage und meldet sich an",
            toastSuccess: "Probefahrt-Anfrage gesendet — der Händler meldet sich kurzfristig.",
        };

    const [form, setForm] = useState({
        buyer_first_name: defaults.first_name || "",
        buyer_last_name: defaults.last_name || "",
        buyer_email: defaults.email || "",
        buyer_phone: defaults.phone || "",
        preferred_date: "",
        message: "",
    });
    const [busy, setBusy] = useState(false);
    // Feb-2026 (User-Wunsch 26 May v3): Nach erfolgreicher Anfrage zeigen wir
    // anonymen Käufern einen sanften Account-Creation-CTA — Lead→User-Conversion.
    const [success, setSuccess] = useState(null); // { email, firstName } | null

    if (!open) return null;
    const upd = (k, v) => setForm((p) => ({ ...p, [k]: v }));
    const reqEmpty = (k) => !String(form[k] || "").trim();
    const reqCls = (k) => (reqEmpty(k) ? "ring-2 ring-red-300 focus:ring-red-500" : "");

    const submit = async () => {
        const missing = ["buyer_first_name", "buyer_last_name", "buyer_email", "buyer_phone"]
            .filter((k) => !String(form[k] || "").trim());
        if (missing.length) {
            toast.error("Bitte alle Pflichtfelder ausfüllen: Vorname, Name, E-Mail, Handynummer.");
            return;
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.buyer_email)) {
            toast.error("Bitte eine gültige E-Mail-Adresse eingeben.");
            return;
        }
        setBusy(true);
        try {
            await api.post("/test-ride-requests", { ...form, listing_id: listingId, request_type: isReserve ? "reserve" : "test_ride" });
            toast.success(labels.toastSuccess);
            // Bei anonymen Käufern (defaults leer) den Account-CTA zeigen,
            // bei eingeloggten Käufern Modal direkt schließen.
            const wasAnonymous = !defaults.email;
            if (wasAnonymous) {
                setSuccess({
                    email: form.buyer_email,
                    firstName: form.buyer_first_name,
                    lastName: form.buyer_last_name,
                    phone: form.buyer_phone,
                });
            } else {
                onClose?.();
            }
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Anfrage fehlgeschlagen — bitte später erneut versuchen.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-[60] bg-black/55 backdrop-blur-sm flex items-end md:items-center justify-center p-3 md:p-6"
            onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
            data-testid="test-ride-modal"
        >
            <div className="bg-white w-full md:max-w-xl rounded-t-md md:rounded-md shadow-2xl border border-gray-200 max-h-[92vh] overflow-y-auto">
                <div className="flex items-start justify-between p-5 border-b border-gray-200 sticky top-0 bg-white z-10">
                    <div>
                        <h2 className="font-display font-bold text-xl text-gray-900 flex items-center gap-2">
                            <KeyRound className="w-5 h-5 text-[#FFCC00]" /> {labels.title}
                        </h2>
                        {listingTitle && (
                            <div className="text-xs text-gray-500 mt-0.5 truncate max-w-[24rem]" data-testid="test-ride-modal-listing">
                                {listingTitle}
                            </div>
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="p-1.5 rounded-sm hover:bg-gray-100"
                        aria-label="Schließen"
                        data-testid="test-ride-modal-close"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>

                <div className="p-5 space-y-4">
                    {success ? (
                        // Feb-2026 (User-Wunsch 26 May v3): Account-Creation-CTA nach
                        // erfolgreicher anonymer Anfrage — Lead → User-Conversion.
                        <div className="text-center py-6 space-y-4" data-testid="test-ride-success-cta">
                            <div className="w-14 h-14 mx-auto rounded-full bg-emerald-100 flex items-center justify-center">
                                <KeyRound className="w-7 h-7 text-emerald-600" />
                            </div>
                            <div>
                                <div className="font-display font-bold text-xl text-gray-900">{labels.successTitle}</div>
                                <div className="text-sm text-gray-600 mt-1">
                                    Hallo <strong>{success.firstName}</strong>, {labels.successBody} <strong>{success.email}</strong>.
                                </div>
                            </div>
                            <div className="bg-gradient-to-br from-[#FFCC00]/20 to-[#FFE799]/20 border border-[#FFCC00] rounded-sm p-4 text-left">
                                <div className="font-bold text-sm text-gray-900 mb-1.5 flex items-center gap-1.5">
                                    <UserIcon className="w-4 h-4" /> Account erstellen — in 30 Sek.
                                </div>
                                <ul className="text-xs text-gray-700 space-y-0.5 list-disc list-inside ml-1">
                                    <li>Anfragen-Verlauf einsehen &amp; Termin verfolgen</li>
                                    <li>Favoriten speichern, Preisalarme einrichten</li>
                                    <li>Schneller absenden — Daten werden gemerkt</li>
                                </ul>
                                <a
                                    href={`/register?email=${encodeURIComponent(success.email)}&first_name=${encodeURIComponent(success.firstName)}&last_name=${encodeURIComponent(success.lastName || "")}&phone=${encodeURIComponent(success.phone || "")}`}
                                    className="mt-3 inline-flex items-center gap-2 px-4 py-2 text-sm font-bold uppercase tracking-wider rounded-sm bg-[#111827] text-[#FFCC00] hover:bg-[#1f2937] w-full justify-center"
                                    data-testid="test-ride-create-account"
                                >
                                    Account in 30 Sek. erstellen
                                </a>
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="mt-2 text-xs text-gray-500 hover:text-gray-800 underline w-full text-center"
                                    data-testid="test-ride-skip-account"
                                >
                                    Nein danke, später
                                </button>
                            </div>
                        </div>
                    ) : (
                    <>
                    <div className="text-xs text-gray-700 bg-[#FFFBEB] border border-[#FFCC00] rounded-sm p-3 leading-relaxed">
                        {labels.intro}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <Label className="text-[11px] uppercase font-bold text-gray-500 inline-flex items-center gap-1">
                                <UserIcon className="w-3 h-3" /> Vorname
                            </Label>
                            <Input
                                value={form.buyer_first_name}
                                onChange={(e) => upd("buyer_first_name", e.target.value)}
                                placeholder="z. B. Max"
                                className={`mt-1 rounded-sm ${reqCls("buyer_first_name")}`}
                                data-testid="test-ride-firstname"
                            />
                        </div>
                        <div>
                            <Label className="text-[11px] uppercase font-bold text-gray-500">Name</Label>
                            <Input
                                value={form.buyer_last_name}
                                onChange={(e) => upd("buyer_last_name", e.target.value)}
                                placeholder="z. B. Mustermann"
                                className={`mt-1 rounded-sm ${reqCls("buyer_last_name")}`}
                                data-testid="test-ride-lastname"
                            />
                        </div>
                        <div>
                            <Label className="text-[11px] uppercase font-bold text-gray-500 inline-flex items-center gap-1">
                                <Mail className="w-3 h-3" /> E-Mail
                            </Label>
                            <Input
                                type="email"
                                value={form.buyer_email}
                                onChange={(e) => upd("buyer_email", e.target.value)}
                                placeholder="max@mustermann.de"
                                className={`mt-1 rounded-sm ${reqCls("buyer_email")}`}
                                data-testid="test-ride-email"
                            />
                        </div>
                        <div>
                            <Label className="text-[11px] uppercase font-bold text-gray-500 inline-flex items-center gap-1">
                                <Phone className="w-3 h-3" /> Handynummer
                            </Label>
                            <Input
                                type="tel"
                                value={form.buyer_phone}
                                onChange={(e) => upd("buyer_phone", e.target.value)}
                                placeholder="+49 170 1234567"
                                className={`mt-1 rounded-sm ${reqCls("buyer_phone")}`}
                                data-testid="test-ride-phone"
                            />
                        </div>
                    </div>

                    <div className="border-t border-gray-100 pt-3">
                        <div className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">Optional</div>
                        <div className="grid grid-cols-1 gap-3">
                            <div>
                                <Label className="text-[11px] uppercase font-bold text-gray-500 inline-flex items-center gap-1">
                                    <Calendar className="w-3 h-3" /> Wunschtermin
                                </Label>
                                <Input
                                    type="date"
                                    value={form.preferred_date}
                                    onChange={(e) => upd("preferred_date", e.target.value)}
                                    className="mt-1 rounded-sm"
                                    data-testid="test-ride-date"
                                />
                            </div>
                            <div>
                                <Label className="text-[11px] uppercase font-bold text-gray-500 inline-flex items-center gap-1">
                                    <MessageSquare className="w-3 h-3" /> Nachricht
                                </Label>
                                <Textarea
                                    value={form.message}
                                    onChange={(e) => upd("message", e.target.value)}
                                    placeholder="z. B. „Hätte am Samstag-Nachmittag Zeit, brauche die Probefahrt für ca. 30 Min."
                                    rows={3}
                                    className="mt-1 rounded-sm"
                                    data-testid="test-ride-message"
                                />
                            </div>
                        </div>
                    </div>
                    </>
                    )}
                </div>

                {!success && (
                <div className="p-4 border-t border-gray-200 bg-gray-50 sticky bottom-0 flex items-center justify-end gap-2">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 text-sm rounded-sm border border-gray-300 bg-white hover:bg-gray-100"
                        data-testid="test-ride-cancel"
                    >
                        Abbrechen
                    </button>
                    <button
                        type="button"
                        onClick={submit}
                        disabled={busy}
                        className="px-5 py-2 text-sm font-bold uppercase tracking-wider rounded-sm bg-[#FFCC00] hover:bg-[#FFD633] text-[#111827] disabled:opacity-60 inline-flex items-center gap-2"
                        data-testid="test-ride-submit"
                    >
                        {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Senden…</> : <><KeyRound className="w-4 h-4" /> {labels.submit}</>}
                    </button>
                </div>
                )}
            </div>
        </div>
    );
}
